import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { SecurityFinding } from '../../../api/client'
import { Alert, Badge, LinkButton } from '../../../components/ui'
import type { BadgeTone } from '../../../components/ui'

// ── Addon status badge ────────────────────────────────────────────────────────

// Eight backend statuses onto the five Badge roles. The old map used blue,
// orange and yellow as well — all three are strays under the colour contract
// (accent/warning/success/danger/neutral), so they fold into brand and amber.
const addonStatusTone: Record<string, BadgeTone> = {
  uploaded:       'neutral',
  pending_review: 'warning',
  queued:         'warning',
  installing:     'accent',
  installed:      'success',
  failed:         'danger',
  updating:       'accent',
  update_failed:  'danger',
}

export function AddonStatusBadge({ status }: { status: string }) {
  return <Badge tone={addonStatusTone[status] ?? 'neutral'}>{status.replace('_', ' ')}</Badge>
}

// Shows a labelled progress bar while a file upload is in flight, then flips
// to a "processing" label once the bytes are on the wire and the server is
// still working (extraction, scanning, job creation).
//
// Deliberately hand-rolled: there is no progress primitive, and this is the
// only determinate progress bar in the app — one instance is not a pattern.
export function UploadProgress({ progress, uploadingLabel, processingLabel }: { progress: number; uploadingLabel: string; processingLabel: string }) {
  const done = progress >= 100
  return (
    <div className="space-y-1.5">
      <div className="text-xs text-gray-500 text-center">{done ? processingLabel : uploadingLabel}</div>
      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full bg-brand-500 transition-all duration-200 ${done ? 'animate-pulse' : ''}`}
          style={{ width: `${Math.max(2, progress)}%` }}
        />
      </div>
    </div>
  )
}

// ── Security findings warning ─────────────────────────────────────────────────

export function SecurityWarning({ findings, quarantined }: { findings: SecurityFinding[]; quarantined?: boolean }) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  if (!findings || findings.length === 0) return null
  const criticalCount = findings.filter((f) => f.severity === 'critical').length
  const warningCount = findings.length - criticalCount
  const tone = quarantined ? 'danger' : 'warning'
  const bodyClass = quarantined ? 'text-red-700' : 'text-amber-700'

  return (
    <Alert tone={tone}>
      <div className="flex items-center justify-between gap-3">
        <span className={`font-medium ${quarantined ? 'text-red-800' : 'text-amber-800'}`}>
          {quarantined
            ? t('detail.addons.security.quarantined', { defaultValue: 'Module quarantined' })
            : t('detail.addons.security.scan', { defaultValue: 'Security scan' })}
          {': '}
          {findings.length === 1
            ? t('detail.addons.security.findingOne', { defaultValue: '1 finding' })
            : t('detail.addons.security.findingMany', { count: findings.length, defaultValue: '{{count}} findings' })}
          {criticalCount > 0 && (
            <span className="text-red-700 ml-1">
              {t('detail.addons.security.criticalCount', { count: criticalCount, defaultValue: '({{count}} critical)' })}
            </span>
          )}
          {warningCount > 0 && (
            <span className="text-amber-600 ml-1">
              {t('detail.addons.security.warningCount', { count: warningCount, defaultValue: '({{count}} warning)' })}
            </span>
          )}
        </span>
        <LinkButton
          tone={quarantined ? 'danger' : 'neutral'}
          className="underline shrink-0"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded
            ? t('detail.addons.security.hide', { defaultValue: 'hide' })
            : t('detail.addons.security.details', { defaultValue: 'details' })}
        </LinkButton>
      </div>
      <p className={`text-xs mt-1 ${bodyClass}`}>
        {quarantined
          ? t('detail.addons.security.quarantinedBody', {
              defaultValue: 'This module has been quarantined due to critical security findings. An admin must approve it before installation.',
            })
          : t('detail.addons.security.reviewBody', { defaultValue: 'Review before installing.' })}
      </p>
      {expanded && (
        <div className="mt-2 space-y-1">
          {findings.map((f, i) => (
            <div key={i} className={`font-mono text-xs rounded px-2 py-1 ${f.severity === 'critical' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
              <span className="font-semibold">
                {f.severity === 'critical'
                  ? t('detail.addons.security.critical', { defaultValue: 'CRITICAL' })
                  : t('detail.addons.security.warning', { defaultValue: 'WARNING' })}
              </span>{' '}
              <span className="font-semibold">{f.file}:{f.line}</span> - {f.label}: <span className={f.severity === 'critical' ? 'text-red-900' : 'text-amber-900'}>{f.snippet}</span>
            </div>
          ))}
        </div>
      )}
    </Alert>
  )
}
